import React, { useState, MouseEvent } from 'react';
import { BackgroundCheckSupportingDocument } from '@indigo-ag/schema';
import { Card, palettes, EllipsisLoader, IconButton, useFlashMessage } from '@indigo-ag/component-module';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload, faFileAlt } from '@fortawesome/pro-regular-svg-icons';

import { createStylesHook } from 'shared/hooks/createStylesHook';
import { downloadFile } from 'shared/utils';

const useStyles = createStylesHook(({ typography: { pxToRem } }) => ({
  documentCard: {
    padding: `0 ${pxToRem(12)}`,
    marginBottom: pxToRem(12),
    height: pxToRem(40),
    width: 'min-content',
    maxWidth: '100%',
    '& button': {
      height: pxToRem(25),
      width: pxToRem(25),
      display: 'inline',
      '&:hover, &:focus': {
        transform: 'scale(1.1)',
        transition: '.2s'
      }
    }
  },
  documentName: {
    fontSize: pxToRem(13),
    color: palettes.brand.gray['700'],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  documentIconBackground: {
    backgroundColor: palettes.brand.blue.base,
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    marginRight: '11px'
  },
  documentIcon: {
    color: palettes.brand.gray.white,
    margin: '2px 5px',
    fontSize: pxToRem(12.8)
  }
}));

export interface SupportingDocumentCardProps {
  documentName: string;
  supportingDocument: BackgroundCheckSupportingDocument;
}

export const SupportingDocumentCard: React.FunctionComponent<SupportingDocumentCardProps> = ({
  documentName,
  supportingDocument
}) => {
  /**
   * Styles
   */
  const innerClasses = useStyles();

  /**
   * State
   */
  const [isDownloading, setDownloading] = useState<boolean>(false);

  const { addFlashMessage } = useFlashMessage();

  if (!supportingDocument.accessibleUrl) {
    return null;
  }

  const handleDownloadDocument = (event: MouseEvent<SVGSVGElement, globalThis.MouseEvent>) => {
    event.preventDefault();
    setDownloading(true);
    downloadFile({ documentName, documentUrl: supportingDocument.accessibleUrl! })
      .catch((error: Error) =>
        addFlashMessage({
          content: error.message,
          title: '',
          variant: 'error'
        })
      )
      .finally(() => setDownloading(false));
  };

  return (
    <Card className={`${innerClasses.documentCard} flex align-items-center`}>
      <div className={innerClasses.documentIconBackground}>
        <FontAwesomeIcon icon={faFileAlt} className={innerClasses.documentIcon} />
      </div>
      <a
        className={innerClasses.documentName}
        href={supportingDocument.accessibleUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span title={documentName}>{documentName}</span>
      </a>

      <div className="flex">
        {isDownloading ? (
          <EllipsisLoader color={palettes.brand.blue.base} style={{ marginLeft: 12, minWidth: 22 }} />
        ) : (
          <IconButton icon={faFileDownload} onClick={handleDownloadDocument} />
        )}
      </div>
    </Card>
  );
};
